// Angular
import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
// Service
import { BroadcastService, ObjectUtils } from '@cv-invision/core';
import { CVfUtilsService } from '../../../../../services/cvf-utils.service';
import { ResourceMediaControlService } from '../../resource-media-control/service/resource-media-control.service';
import { LayoutRenderService } from '../../../../services/layout-render.service';
import { SmartTagsService } from '../services/smart-tags.service';
// Component
import { CascaderComponent } from '@cv-invision/cv.component-library.ux';
// Model
import { SmartTagTemplate } from '../model/smart-tag-template.model';
import { ResourceModel } from '../../resource-media-control/model/resource-media.model';
import { FormGroup } from '@angular/forms';
import { SmartTagTemplateModelClass } from '../model';

@Component({
	selector: 'cv-schema-render-smart-tag-form',
	templateUrl: './smart-tag-form.component.html',
	providers: [ ResourceMediaControlService ]
})
export class SmartTagFormComponent implements OnInit, AfterViewInit {
	@ViewChild('cvCascader', { static: false }) cvCascader: CascaderComponent;
	@Input() data: any;
	@Input() resourceModel: ResourceModel = new ResourceModel();
	@Input() filterBy: any;

	// Cascader Options
	public cascaderConfigOptions = { value: 'key', text: 'value', children: 'children' };

	// Type of Smart Tags
	public smartTagTypeList: any = [];
	public smartTagTemplateType = SmartTagTemplate;
	public smartTagData: any = {};

	public element = '';

	public selectedSmartTagItem: any = null;
	public smartTagTemplate: SmartTagTemplate = null;

	// Keep the original Smart Tag Template, then clean the object after save on Edition
	public originalSmartTagTemplate: SmartTagTemplate = null;
	public originalSmartTagTemplateFormControls: any = [];

	constructor(
		private broadcastService: BroadcastService,
		private cvfUtils: CVfUtilsService,
		private resourceMediaControlService: ResourceMediaControlService,
		private layoutRenderService: LayoutRenderService,
		private smartTagsService: SmartTagsService
	) {
	}

	ngOnInit() {
		// Init SmartTagTypes
		const smartTagTypeList = ObjectUtils.recursiveDeepCopy(this.layoutRenderService.getExtraDataOption('smartTagTypes') || []);
		const imageless = this.resourceModel.smartTagForm?.imageless;
		let filteredSmartTagTypeList = this.smartTagsService.getSmartTagTypes(this.data, smartTagTypeList, imageless);
		// Filter at Component Level
		if (this.filterBy && this.filterBy !== null && this.filterBy.length > 0) {
			filteredSmartTagTypeList = filteredSmartTagTypeList.filter((type: any) => this.filterBy.indexOf(type.key) >= 0);
		}

		this.smartTagTypeList = filteredSmartTagTypeList;
	}

	/**
	 * Wait for the UI to load
	 * Validate if there is Smart Tag Data to Edit
	 */
	ngAfterViewInit() {
		// If Smart Tag Data is not Null, it means it has previous Data
		if (this.resourceModel.smartTagForm.smartTagData !== null) {
			setTimeout(() => {
				this.loadSmartTagEditData();
			});
		}
	}

	/**
	 * If Smart Tag Data is not Null, we are Editing an existing Smart Tag
	 * @private
	 */
	private loadSmartTagEditData(): void {
		// Clone the Object
		this.smartTagData = ObjectUtils.recursiveDeepCopy(this.resourceModel.smartTagForm.smartTagData);

		// Item can be only calculated from the Sub Category or the Title
		if (
			(this.smartTagData.subCategory !== null && this.smartTagData.subCategory !== '') ||
			(this.smartTagData.title !== null && this.smartTagData.title !== '')
		) {
			// Get the Smart Tag Item
			const smartTagValue = this.smartTagData.subCategory || this.smartTagData.title;
			this.selectedSmartTagItem = this.selectSmartTagItem(this.smartTagData.tagType);
			// If found Match Select the Element
			if (this.selectedSmartTagItem && this.selectedSmartTagItem !== null) {
				const tagTemplate = this.getSelectedViewMode(this.selectedSmartTagItem);
				this.smartTagTemplate = tagTemplate;
				this.originalSmartTagTemplate = tagTemplate;
				if (this.originalSmartTagTemplate === SmartTagTemplate.RENDER_FORM_RENDER) {
					this.originalSmartTagTemplateFormControls = this.cvfUtils.getSelectedFormControls(this.selectedSmartTagItem);
				}
				// Change the Placeholder to represent the selected Element
				this.cvCascader.placeholder = smartTagValue;
				// Change the value to represent the selected Element
				this.element = this.selectedSmartTagItem.subCategory.key;
				// Execute the Save
				this.saveSmartTagForm();
			} else {
				// If not, print a warning error to better identify the problem
				console.warn(`Subcategory: ${this.smartTagData.subCategory} or Title: ${this.smartTagData.title}  Does not exists on the UI Smart Tags Available`);
			}
		}
	}

	/**
	 * UpdateElement
	 * update element value subscribing to the emitter of cascader
	 */
	public onSelectSmartTag(event: any): any {
		// Search for the Type
		this.selectedSmartTagItem = this.selectSmartTagItem(event.key);

		// Change the value to newly selected element.
		this.element = this.selectedSmartTagItem.subCategory.key

		const tagTemplate = this.getSelectedViewMode(this.selectedSmartTagItem);
		// If type is different from the previous one, remove all properties from the Object that match that type
		if (
			this.smartTagTemplate !== null &&
			// this.smartTagTemplate !== tagTemplate &&
			this.resourceModel.smartTagForm.selectedSmartTagItem !== this.selectedSmartTagItem &&
			this.smartTagTemplate !== SmartTagTemplate.DEFAULT
		) {
			// we do this to remove properties that do not belong to the Template
			const renderer = new SmartTagTemplateModelClass[this.smartTagTemplate];
			for (const [key, value] of Object.entries(renderer)) {
				if (this.smartTagData[key]) {
					delete this.smartTagData[key];
				}
			}

			// Render Form are dynamic, they don't have Models per se, so kill everything inside it
			if (this.smartTagTemplate === SmartTagTemplate.RENDER_FORM_RENDER) {
				const formControls = this.resourceModel.smartTagForm.smartTagTemplateChangeFromFields || [];
				for (const formControl of formControls) {
					delete this.smartTagData[formControl.field];
				}

				for (const [key, value] of Object.entries(this.smartTagData)) {
					if (key !== '_Path') {
						delete this.smartTagData[key];
					}
				}
			}

			this.smartTagData.description = '';
		}
		// Select the new Tag Template
		this.smartTagTemplate = tagTemplate;

		this.saveSmartTagForm();
	}

	/**
	 * Get from the selected Smart Tag the possible View Mode if Available
	 * @param selectedSmartTagItem
	 */
	public getSelectedViewMode(selectedSmartTagItem: any): any {
		return this.cvfUtils.getSelectedViewMode(selectedSmartTagItem);
	}

	/**
	 * Select parent from the Event Key
	 * @param eventKey
	 * @private
	 */
	private selectSmartTagItem(eventKey: string, eventValue?: string): any {
		return this.cvfUtils.getSmartTagItem(this.smartTagTypeList, eventKey, eventValue);
	}

	/**
	 * On Every change validate the data is correct and emit the value
	 */
	public validateData(smartTagForm: any): void {
		// Validate that emitted Form is valid/ready by the Form Group
		if (smartTagForm.valid) {
			this.smartTagData = { ...this.smartTagData, ...smartTagForm.value };
			this.saveSmartTagForm();
		} else {
			console.log('smart tag form is invalid');
		}
	}

	/**
	 * Save the Smart Tag Form
	 * Including the inner Template Data + Basic SmartTag
	 * Also the selected Smart Tag Type
	 */
	public saveSmartTagForm(): void {
		this.resourceModel.smartTagForm = {
			selectedSmartTagItem: this.selectedSmartTagItem,
			smartTagData: this.smartTagData,
			smartTagTemplateChangeFrom: this.originalSmartTagTemplate,
			smartTagTemplateChangeFromFields: this.originalSmartTagTemplateFormControls
		};

		this.resourceMediaControlService.resourceModelChange.next(this.resourceModel);
	}
}
